'use client'

import type { RentalTerm } from '@repo/core/rental-terms'
import { useMemo, useState } from 'react'

import {
  type RentalItemView,
  type RentalSort,
  type RentalTermView,
  sortRentals,
} from '@/lib/rental'
import { matchesStems, queryStems } from '@/lib/rental-search'
import { useDebouncedValue } from '@/lib/use-debounced-value'

export function useRentalListing(items: RentalItemView[]) {
  const [active, setActive] = useState<string[]>([])
  /** One of spec §11's five lengths, or `null` for "just show me the rates". */
  const [term, setTerm] = useState<RentalTerm | null>(null)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<RentalSort>('polecane')
  // The field repaints per keystroke; the FILTERING waits for a pause, so the
  // live region announces one result instead of one per letter.
  const settledQuery = useDebouncedValue(query)

  /** Text search alone — the set the category counts are taken over. */
  const searched = useMemo(() => {
    // Name, blurb and category label together, so "aparat" or "mikrofon" find
    // things even though neither word appears in a product name — and read as
    // a sentence, so „potrzebuję mikrofonu" works too (see `rental-search`).
    const stems = queryStems(settledQuery)
    if (stems.length === 0) return items
    return items.filter((i) => matchesStems(i, stems))
  }, [items, settledQuery])

  const visible = useMemo(() => {
    const byCategory =
      active.length === 0 ? searched : searched.filter((i) => active.includes(i.categorySlug))
    return sortRentals(byCategory, sort)
  }, [active, searched, sort])

  /**
   * How many items each pill would show. Taken over the SEARCH result rather
   * than the final list, so a pill reports what pressing it does — counting
   * the already-filtered set would make every unpressed pill read zero.
   */
  const counts = useMemo(() => {
    const map = new Map<string, number>()
    for (const item of searched) map.set(item.categorySlug, (map.get(item.categorySlug) ?? 0) + 1)
    return map
  }, [searched])

  const days = term ?? 0

  /**
   * The catalogue's price for the chosen length — never a computed one.
   *
   * `null` when no length is set, or when this item is not sold for it. Before
   * I4 this multiplied a daily rate and applied a rung, which was the browser
   * doing arithmetic the checkout did differently.
   */
  const quoteFor = (item: RentalItemView): RentalTermView | null =>
    term === null ? null : (item.terms.find((row) => row.days === term) ?? null)

  const toggle = (key: string) =>
    setActive((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))

  // Reads the SETTLED query, not the raw one: gating on the raw value made the
  // reset control appear the instant a key went down, 200ms before the grid
  // reacted to the typing that summoned it.
  const filtered = settledQuery.trim().length > 0 || active.length > 0

  // The term is not a filter, so clearing "filters" deliberately leaves it be.
  const resetFilters = () => {
    setQuery('')
    setActive([])
  }

  return {
    active,
    counts,
    days,
    filtered,
    query,
    quoteFor,
    resetFilters,
    setQuery,
    setSort,
    setTerm,
    settledQuery,
    sort,
    term,
    toggle,
    visible,
  }
}
